// cSpell: ignore Augmentor servicepre ungzip
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { Injectable, InjectionToken, inject } from "@angular/core";
import { getLoggerFor, Logger, LogInvocation, LogLevel, LogName } from "@tylertech/logging";
import { StringHelper, WINDOW } from "@tylertech/shared-module";
import { AfterSaveContext, ExecutionResult, IDocument, RecalculateReturn } from "@tylertech/tyler-api-types";
import * as pako from "pako";
import { from, Observable, of, Subscriber, throwError } from "rxjs";
import { catchError, map, switchMap, tap, } from "rxjs/operators";
import { ErrorInfo } from "../interfaces";
import { RouteInfoService } from "./routeInfo.service";
import { ErpStorageKeys } from "./security.service";

export interface IDispatchCriteria<T = unknown>
{
    Endpoint: string;
    Model?: T;
    FilterString?: string;
}

export interface DispatchInterceptor
{
    canIntercept(className: string): Promise<boolean>;
    intercept(className: string, methodName: string, jsonData: string, filter: string): Promise<string>;
}

export interface DispatchAugmentor
{
    canAugment(className: string, methodName: string): Promise<boolean>;
    augment(className: string, methodName: string, currentReturn: string): Promise<string>;
}

declare global
{
    interface Window
    {
        /**
         * This object is set by the Erp-Pro 10 Embedded Html Control.
         */
        readonly dispatchInterceptor?: DispatchInterceptor;
        /**
         * This object is set by the Erp-Pro 9 Embedded Html Control.
         */
        readonly dispatchAugmentor?: DispatchAugmentor;
    }
}

/**
 * Enables the dispatchService to compress request body if over the defined limit.
 */
export const ALLOW_COMPRESSION = new InjectionToken(
    "ALLOW_COMPRESSION",
    {
        providedIn: "root",
        factory: () => true
    });

/**
 * The number of characters allowed in a request body before it must be compressed.
 */
export const MAX_UNCOMPRESSED_THRESHOLD = new InjectionToken(
    "MAX_UNCOMPRESSED_THRESHOLD",
    {
        providedIn: "root",
        factory: () => 100000
    });

export interface CefSharpObject
{
    BindObjectAsync(name: string): Promise<void>;
}

export interface Window extends globalThis.Window
{
    readonly CefSharp?: CefSharpObject;
}

/** Just a dump class to encapsulate the global value's calls in Observables. */
class EmbeddedInterceptor
{
    private interceptor: DispatchInterceptor | null;
    constructor(private readonly window: Window)
    { }

    private async getInterceptor(): Promise<DispatchInterceptor>
    {
        if (this.interceptor)
        {
            return this.interceptor;
        }

        if (!this.window.CefSharp)
        {
            return undefined;
        }

        try
        {

            if (this.window.dispatchInterceptor)
            {
                // For old style in ERP Pro App and Shell
                this.interceptor = this.window.dispatchInterceptor;
                Logger.debug("window.dispatchInterceptor object bound successfully.");
            }
            else
            {
                // For new style in ERP Pro app
                await this.window.CefSharp.BindObjectAsync("dispatchInterceptor");
                this.interceptor = this.window.dispatchInterceptor;
                Logger.debug("CefSharp defaultInterceptor object bound successfully.");
            }
        }
        catch (error)
        {
            // Handles the "Object(s) already bound" status gracefully
            if (typeof this.window.dispatchInterceptor === 'undefined')
            {
                Logger.error("CefSharp binding for defaultInterceptor failed:", error);
            }
        }

        return this.interceptor;
    }

    canIntercept(serviceName: string): Observable<boolean>
    {
        return from(this.getInterceptor())
            .pipe(switchMap((interceptor) =>
            {
                return interceptor
                    ? from(this.interceptor.canIntercept(serviceName))
                    : of(false);
            }));
    }

    intercept(serviceName: string, methodName: string, jsonData: string, filter: string): Observable<string>
    {
        return from(this.getInterceptor())
            .pipe(switchMap((interceptor) =>
            {
                if (!interceptor)
                {
                    throw new Error("DispatchError: there is not interceptor defined.");
                }

                return interceptor.intercept(serviceName, methodName, jsonData, filter);
            }));
    }
}

/** Just a dump class to encapsulate the global value's calls in Observables. */
class EmbeddedAugmentor
{
    constructor(
        private readonly augmentor: DispatchAugmentor)
    { }

    canAugment(className: string, methodName: string): Observable<boolean>
    {
        return this.augmentor
            ? from(this.augmentor.canAugment(className, methodName))
            : of(false);
    }

    augment(className: string, methodName: string, currentReturn: string): Observable<string>
    {
        if (!this.augmentor)
        {
            throw new Error("DispatchError: there is not augmentor defined.");
        }

        return from(this.augmentor.augment(className, methodName, currentReturn));
    }
}
export function tryParseBody<T>(response: HttpResponse<Blob>): Observable<T | string>
{
    if (!(response.body instanceof Blob))
    {
        return of(response.body);
    }

    if (response.headers.has("BinaryType"))
    {
        const type = response.headers.get("BinaryType");
        if (type === "RecalculateReturn")
        {
            const document: IDocument = {
                Content: '',
                Filename: response.headers.get("Filename"),
                MimeType: response.headers.get("MimeType"),
                ReferenceId: response.headers.get("ReferenceId"),
            };
            document["$Blob"] = response.body;

            const after: AfterSaveContext = {
                DocumentsToDownload: [
                    document
                ],
                Halt: undefined,
                LookupsToRefresh: undefined,
                PostAction: undefined,
                SaveConfirmationHeader: undefined,
                SaveConfirmationMessage: undefined,
                ShowAsToast: undefined,
            };

            const ret: RecalculateReturn = {
                AfterSaveContext: after,
                Entity: {}
            };
            return of(ret as T);
        }
    }

    return from(response.body.text())
        .pipe(
            map(x =>
            {
                try
                {
                    return JSON.parse(x);
                }
                catch
                {
                    return x;
                }
            }),
            tap({
                error: err =>
                {
                    getLoggerFor("tryParseBodyImpl").error(err);
                }
            }));
}

export function parseErrorFromBlob<T = unknown>(err: HttpErrorResponse): Observable<HttpResponse<T>>
{
    if (!(err.error instanceof Blob))
    {
        Logger.error(err);
        return throwError(() => err);
    }

    const reader: FileReader = new FileReader();
    const observable = new Observable((observer: Subscriber<HttpResponse<T>>) =>
    {
        reader.onloadend = (_ev: ProgressEvent<FileReader>) =>
        {
            const stringResult = reader.result.toString();
            let error: unknown;
            if (stringResult.indexOf("504 Gateway") > -1)
            {
                error = { Message: "Unable to complete request within the allowed network timeout period. The server is still processing the request." };
            }
            else
            {
                error = JSON.parse(stringResult);
            }

            observer.error(new HttpErrorResponse({ error: error }));
            observer.complete();
        };
    });
    reader.readAsText(err.error);
    return observable;
}

function compressBody(data: string | undefined): ArrayBufferLike | undefined
{
    return data
        ? pako.gzip(data).buffer
        : undefined;
}

export const enum ServicePrefix
{
    NoAuthorizationRequired = "servicepre",
    AuthorizationRequired = "service",
}

@Injectable()
export class DispatchService
{
    private readonly allowCompression = inject(ALLOW_COMPRESSION);
    private readonly uncompressedLimit = inject(MAX_UNCOMPRESSED_THRESHOLD);
    private readonly $http = inject(HttpClient);
    private readonly $routeInfoService = inject(RouteInfoService);

    private readonly log = getLoggerFor("DispatchService");
    private readonly window = inject(WINDOW) as Window;

    private readonly dispatchAugmentor = new EmbeddedAugmentor(window.dispatchAugmentor);
    private readonly dispatchInterceptor = new EmbeddedInterceptor(this.window);

    dispatch<TModel, TReturn>(
        @LogName("criteria") criteria: IDispatchCriteria<TModel>,
        nonAuthenticatedRequest: boolean = false): Observable<HttpResponse<TReturn>>
    {
        const strings: string[] = criteria.Endpoint.split("/");
        const serviceName: string = strings[strings.length - 2];
        const methodName: string = strings[strings.length - 1];
        const filter = criteria.FilterString;

        return this.tryAugment(serviceName, methodName, filter, criteria.Model, nonAuthenticatedRequest);
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private dispatchImpl<TReturn>(
        serviceName: string,
        methodName: string,
        filter: string,
        @LogName("model") model: unknown,
        nonAuthenticatedRequest: boolean = false
    ): Observable<HttpResponse<TReturn>>
    {
        const servicePrefix = nonAuthenticatedRequest
            ? ServicePrefix.NoAuthorizationRequired
            : ServicePrefix.AuthorizationRequired;
        let headers = new HttpHeaders();
        let url = `/${servicePrefix}/${serviceName}/${methodName}`;
        if (filter)
        {
            url = url + "?" + filter;
        }

        const tenant = this.$routeInfoService.tenant;
        if (tenant)
        {
            headers = headers.set("Tenant", tenant)
                .set("TenantId", tenant);
        }

        for (const key of Object.values(ErpStorageKeys))
        {
            const value = window.sessionStorage.getItem(key);
            if (value !== null)
                headers = headers.set(key, value);
        }

        const [finalHeaders, reqBody] = this.applyCompression(headers, model);

        const request = this.$http.post(
            url,
            reqBody,
            {
                headers: finalHeaders,
                observe: "response",
                responseType: 'blob',
            });

        return request.pipe(
            tap(res => this.log.debug(`dispatchImpl:Response`, { res })),
            switchMap(res => tryParseBody(res)
                .pipe(
                    map(parsedBody =>
                    {
                        this.log.debug(`response parsedBody`, parsedBody);
                        return res.clone({
                            body: parsedBody as TReturn
                        });
                    }),
            )),
            catchError(parseErrorFromBlob<TReturn>));

    }

    private applyCompression(headers: HttpHeaders, body: unknown): [HttpHeaders, ArrayBufferLike | string | undefined]
    {
        const bodyStr = StringHelper.stringify(body);
        if (!this.allowCompression
            || bodyStr?.length <= this.uncompressedLimit)
        {
            return [headers, bodyStr];
        }

        return [
            headers.set("Content-Encoding", "gzip")
                .set("Content-Type", "application/gzip"),
            compressBody(bodyStr)
        ];
    }

    // @LogInvocation(LogLevel.DEBUG, true)
    private tryAugment<TReturn>(
        serviceName: string,
        methodName: string,
        filter: string,
        @LogName("model") model: unknown,
        requireAuthentication: boolean
    ): Observable<HttpResponse<TReturn>>
    {
        return this.dispatchAugmentor.canAugment(serviceName, methodName)
            .pipe(
                switchMap((canAugment: boolean) =>
                {
                    if (!canAugment)
                    {
                        return this.tryIntercept<TReturn>(serviceName, methodName, filter, model, requireAuthentication);
                    }

                    return this.dispatchImpl<TReturn>(serviceName, methodName, filter, model, requireAuthentication)
                        .pipe(
                            switchMap((r: HttpResponse<TReturn>) =>
                            {
                                return this.dispatchAugmentor.augment(serviceName, methodName, JSON.stringify(r));
                            }),
                            map(x =>
                            {
                                return JSON.parse(x) as HttpResponse<TReturn>;
                            }));
                }));
    }

    // @LogInvocation(LogLevel.DEBUG, true)
    private tryIntercept<TReturn>(
        serviceName: string,
        methodName: string,
        filter: string,
        @LogName("model") model: unknown,
        requireAuthentication: boolean
    ): Observable<HttpResponse<TReturn>>
    {
        return this.dispatchInterceptor.canIntercept(serviceName)
            .pipe(
                switchMap(canIntercept =>
                {
                    if (!canIntercept)
                    {
                        return this.dispatchImpl<TReturn>(serviceName, methodName, filter, model, requireAuthentication);
                    }

                    return this.dispatchInterceptor.intercept(serviceName, methodName, StringHelper.stringify(model), filter)
                        .pipe(
                            map((d: string) =>
                            {
                                const body: Partial<HttpResponse<unknown>> = {
                                    body: JSON.parse(d) as TReturn,
                                };
                                return body as HttpResponse<TReturn>;
                            }),
                            catchError((err: unknown) =>
                            {
                                let message = "unknown";
                                if (err && typeof err === "string")
                                {
                                    message = err.split(/\r?\n/)[0];
                                }

                                const errorInfo: ErrorInfo = {
                                    Message: `DispatchInterceptError: ${message}`,
                                    StackTrace: err as string,
                                };

                                return throwError(() => new HttpErrorResponse({ error: errorInfo }));
                            }));
                }));
    }
}

export function isHttpResponseError(error: unknown): error is HttpErrorResponse
{
    return !!error
        && typeof error === "object"
        && "error" in error
        && isErrorInfo((error as Record<"error", unknown>).error);
}

export function isErrorInfo(obj: unknown): boolean
{
    return !!obj
        && typeof obj === "object"
        && "Message" in obj;
}

export function isExecutionResult<T = unknown>(error: unknown): error is ExecutionResult<T>
{
    return !!error
        && typeof error === "object"
        && "Success" in error
        && "Result" in error;
}

export function isErroredExecutionResult(error: unknown): error is ExecutionResult<void>
{
    return !!error
        && typeof error === "object"
        && "Success" in error
        && "ErrorMessage" in error
        && !!(error as ExecutionResult<void>).ErrorMessage;
}
